/**
 * ======================================================================
 * MASTER FRAMEWORK - the ONE generic live worklist table.
 *
 * A single component renders EVERY worklist source. It owns the shared 90% - the
 * list-page toolbar (Filters + Columns + Search + Export), the DataTable, per-
 * user column/filter preferences, pagination - and delegates the per-source 10%
 * (which columns, which query, where a row links, how it exports) to the source
 * descriptor in `live-sources.tsx`.
 *
 * This replaces the five near-identical `live-*-table.tsx` files. Adding a new
 * source no longer means a new component and a new branch in the dashboard page:
 * it is one entry in `LIVE_SOURCES`. "Keep the designs, fix the infrastructure" -
 * every source still renders the full list-page experience it did before; it is
 * just driven by data now, from one place.
 *
 * Hooks safety: the descriptor exposes `useData` as a hook. The caller keys this
 * component by `source`, so a mounted instance never sees its source change - the
 * same hook is called in the same order on every render.
 * ======================================================================
 */

import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Download } from 'lucide-react'

import {
  DataTable,
  type ActiveFilter,
} from '@/admin/components/data-table'
import { Button } from '@/shared/components/ui/button'
import { ListPageToolbar } from '@/admin/components/list-page-toolbar'
import { ProductFilterMenu } from '@/shared/components/ui/product-filter-menu'
import { SearchInput } from '@/shared/components/ui/search-input'
import { useTableColumnPreferences } from '@/admin/hooks/use-table-column-preferences'
import { useTableFilterPreferences } from '@/admin/hooks/use-table-filter-preferences'
import { useFormatters } from '@/shared/lib/formatting'
import type { DataSetRef } from './dashboard-types'
import { getLiveSource, type LiveRow, type LiveSourceContext } from './live-sources'
import { HeaderControls } from './tile-header'

/**
 * Default rows per page in a live tile; the table scrolls within its own box.
 * One of the shared pager's page-size options (10 / 20 / 30 / 50).
 */
const TILE_PAGE_SIZE = 20

export function LiveTable({
  source,
  binding,
  tableId,
  manage = false,
}: {
  source: string
  binding: DataSetRef
  tableId: string
  /**
   * Whether to show the view-management controls (columns / filters / search).
   * A dashboard widget is a compact preview - at rest it shows only the export
   * action; the full toolbar appears when the tile is expanded (`manage`).
   */
  manage?: boolean
}) {
  const src = getLiveSource(source)
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { formatDate } = useFormatters()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([])
  const [filterValues, setFilterValues] = useTableFilterPreferences(tableId)
  const {
    columnVisibility,
    setColumnVisibility,
    saveColumnVisibility,
    columnOrder,
    setColumnOrder,
    saveColumnOrder,
  } = useTableColumnPreferences(tableId)

  // A tile shows a full page of rows inside the table's fixed-height scroll box
  // (`maxHeightClass` below), so it scrolls instead of stopping after a handful
  // of rows, and pages on with the shared pager. The page size is the user's
  // pick from the pager; a seeded `binding.limit` (from before tiles scrolled)
  // no longer caps it.
  const [limit, setLimit] = useState(TILE_PAGE_SIZE)
  // Safe: the caller keys <LiveTable> by `source`, so `src` (and thus
  // whether this hook is called) is stable for the life of a mounted
  // instance - same rule as `src.useData` below. When a source declares
  // `useColumns`, it wins over `buildColumns` so the tile renders the exact
  // same column definitions as the source's own list page.
  const injectedColumns = src?.useColumns?.(binding)
  const columns = useMemo(
    () => injectedColumns ?? (src ? src.buildColumns(formatDate, binding) : []),
    [injectedColumns, src, formatDate, binding],
  )
  const ctx: LiveSourceContext = {
    binding,
    page,
    limit,
    search,
    activeFilters,
    filterValues,
    columns,
    columnVisibility,
  }
  // Safe: the caller keys <LiveTable> by `source`, so `src` (and thus this hook)
  // is stable for the life of a mounted instance. An unknown source is a no-op
  // hook that returns an empty result, keeping the call order constant.
  const result = src
    ? src.useData(ctx)
    : {
        rows: [] as LiveRow[],
        totalCount: 0,
        filterFacets: [],
        isPending: false,
        isFetching: false,
        isError: false,
        refetch: () => {},
        exportCsv: () => {},
        isExporting: false,
      }

  const currentConfig = useMemo(
    () => ({ columnVisibility, columnOrder, filterValues }),
    [columnVisibility, columnOrder, filterValues],
  )

  if (!src) return null

  return (
    <div>
      <HeaderControls>
      {manage ? (
      <ListPageToolbar<LiveRow>
        tableId={tableId}
        layout="compact"
        showViews={false}
        search={search}
        onSearchChange={(v) => {
          setSearch(v)
          setPage(1)
        }}
        searchPlaceholder={src.searchPlaceholder}
        views={[]}
        activeViewUuid={null}
        onSelectView={() => {}}
        onSaveView={() => {}}
        onDeleteView={() => {}}
        onUpdateView={() => {}}
        onRenameView={() => {}}
        currentConfig={currentConfig}
        columns={columns}
        activeFilters={activeFilters}
        onFiltersChange={(f) => {
          setActiveFilters(f)
          setPage(1)
        }}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={(v) => {
          setColumnVisibility(v)
          saveColumnVisibility(v)
        }}
        columnOrder={columnOrder}
        onColumnOrderChange={(o) => {
          setColumnOrder(o)
          saveColumnOrder(o)
        }}
        filterFacets={result.filterFacets}
        filterValues={filterValues}
        onFilterValuesChange={(v) => {
          setFilterValues(v)
          setPage(1)
        }}
        onExport={result.exportCsv}
        isExporting={result.isExporting}
      />
      ) : (
        // Dashboard widget at rest: Search + Filters + Export, so a card can be
        // searched and filtered without expanding it (WPS-232 #5) - the same
        // controls the Activity card has. Columns appear when it is expanded.
        // Filters renders nothing when the source has no facets.
        <div className="flex min-w-0 items-center gap-2" data-tour="dashboard-card-filters">
        <SearchInput
          value={search}
          onChange={(v) => {
            setSearch(v)
            setPage(1)
          }}
          placeholder={src.searchPlaceholder}
          aria-label={src.searchPlaceholder}
          className="h-9 w-[200px] min-w-0 text-[13px] max-sm:w-[140px]"
        />
        <ProductFilterMenu
          facets={result.filterFacets}
          value={filterValues}
          onChange={(v) => {
            setFilterValues(v)
            setPage(1)
          }}
          triggerClassName="h-9 rounded-[9px] border-[0.5px] border-[var(--rule)] px-3 text-[13px]"
        />
        <Button
          variant="ghost"
          onClick={() => result.exportCsv?.()}
          disabled={result.isExporting || !result.exportCsv}
          aria-label="Export"
          title="Export to Excel"
          className="flex h-9 items-center gap-2 rounded-[9px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] px-3 text-[13px] font-normal text-[var(--ink)] transition-colors hover:border-[var(--rule-strong)] hover:bg-[var(--bg-card)]"
        >
          <Download size={15} />
        </Button>
        </div>
      )}
      </HeaderControls>
      <div>
        <DataTable<LiveRow>
          hideFullscreen
          tableId={tableId}
          columns={columns}
          data={result.rows}
          totalCount={result.totalCount}
          isLoading={result.isPending}
          isFetching={result.isFetching}
          isError={result.isError}
          onRetry={result.refetch}
          {...(src.paginated
            ? {
                page,
                pageSize: limit,
                onPageChange: setPage,
                // The shared pager only renders when it can also change the
                // page size; without this the tile had no pager at all.
                onPageSizeChange: (size: number) => {
                  setLimit(size)
                  setPage(1)
                },
              }
            : {})}
          columnVisibility={columnVisibility}
          columnOrder={columnOrder}
          onRowClick={(r) => {
            const href = src.rowHref(r)
            if (href) navigate(href)
          }}
          rowKey="uuid"
          maxHeightClass="max-h-[440px]"
          dense
          emptyMessage={t('dashboard.noResults', 'No results meet the parameter.')}
        />
      </div>
    </div>
  )
}
